import { useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { GraduationCap, Video, BookOpen, Users, BellRing } from "lucide-react";

const ECWID_PRODUCT_ID = "837174377";
const ECWID_SCRIPT_SRC =
  "https://app.ecwid.com/script.js?2289132&data_platform=singleproduct_v2";

// Static product markup — pinned to compile-time constants only (no user
// input), so it is safe for `dangerouslySetInnerHTML`. Raw HTML is required
// because Ecwid's loader looks up specific `class` attributes that JSX would
// rename to `className`.
const ECWID_PRODUCT_HTML = `
  <div class="ecsp ecsp-SingleProduct-v2 ecsp-SingleProduct-v2-bordered ecsp-SingleProduct-v2-centered ecsp-Product ec-Product-${ECWID_PRODUCT_ID}"
       itemscope itemtype="http://schema.org/Product"
       data-single-product-id="${ECWID_PRODUCT_ID}">
    <div itemprop="image"></div>
    <div class="ecsp-title" itemprop="name" content="Bayesian options trading full membership"></div>
    <div itemtype="http://schema.org/Offer" itemscope itemprop="offers">
      <div class="ecsp-productBrowser-price ecsp-price" itemprop="price" content="1210" data-spw-price-location="button">
        <div itemprop="priceCurrency" content="EUR"></div>
      </div>
    </div>
    <div customprop="options"></div>
    <div customprop="qty"></div>
    <div customprop="addtobag"></div>
    <div customprop="vatinprice"></div>
  </div>
`;

function EcwidProduct() {
  useEffect(() => {
    // Inject the Ecwid loader script. Once loaded we call xProduct() to
    // render the widget into the placeholder rendered below.
    const script = document.createElement("script");
    script.src = ECWID_SCRIPT_SRC;
    script.async = true;
    script.charset = "utf-8";
    script.setAttribute("data-cfasync", "false");
    script.onload = () => {
      if (typeof window.xProduct === "function") {
        try {
          window.xProduct();
        } catch (err) {
          console.warn("Ecwid xProduct() init failed:", err);
        }
      }
    };
    script.onerror = (err) => {
      console.warn("Ecwid script failed to load:", err);
    };
    document.body.appendChild(script);

    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, []);

  return (
    <div
      className="ecwid-embed-host"
      data-testid="ecwid-embed"
      dangerouslySetInnerHTML={{ __html: ECWID_PRODUCT_HTML }}
    />
  );
}

export default function OurOfferPage() {
  const { user } = useAuth();
  // Full members don't need an upgrade page. Redirect them away.
  if (user?.role === "user") {
    return <Navigate to="/stocks-in-play" replace />;
  }
  return (
    <div className="max-w-4xl space-y-10" data-testid="our-offer-page">
      <div>
        <div className="text-xs font-medium tracking-[0.2em] uppercase text-slate-500 mb-2">
          Membership
        </div>
        <h2 className="text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 font-heading">
          Become a full member of the desk.
        </h2>
        <p className="text-slate-600 mt-4 max-w-2xl leading-relaxed">
          Guests can browse the platform with anonymised tickers. Full members
          see every real ticker, can record their own trades, and unlock every
          action button on the site.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 lg:col-span-2">
          <div className="text-xs font-medium tracking-[0.2em] uppercase text-slate-500 mb-2">
            One-time onboarding
          </div>
          <div className="flex items-end gap-2 mb-1">
            <div className="font-heading font-black tracking-tighter text-5xl text-slate-900 tabular-nums">
              €1,000
            </div>
            <div className="text-sm text-slate-500 mb-1.5">ex VAT</div>
          </div>
          <p className="text-sm text-slate-600 mb-6">
            Includes a full first year of membership.
          </p>
          <ul className="space-y-3 text-sm text-slate-700">
            <li className="flex items-start gap-3">
              <Video className="w-4 h-4 text-slate-500 mt-0.5" strokeWidth={1.75} />
              Live training sessions via Zoom
            </li>
            <li className="flex items-start gap-3">
              <GraduationCap className="w-4 h-4 text-slate-500 mt-0.5" strokeWidth={1.75} />
              On-demand training videos
            </li>
            <li className="flex items-start gap-3">
              <BookOpen className="w-4 h-4 text-slate-500 mt-0.5" strokeWidth={1.75} />
              Companion eBook
            </li>
            <li className="flex items-start gap-3">
              <Users className="w-4 h-4 text-slate-500 mt-0.5" strokeWidth={1.75} />
              Monthly Zoom sessions after option expiration day to trade, check
              positions and go through the watchlist and general market
              conditions together
            </li>
            <li className="flex items-start gap-3">
              <BellRing className="w-4 h-4 text-slate-500 mt-0.5" strokeWidth={1.75} />
              Trade alerts through Discord and WhatsApp
            </li>
          </ul>
        </div>

        <div className="bg-slate-900 text-white rounded-xl shadow-sm p-6 flex flex-col">
          <div className="text-xs font-medium tracking-[0.2em] uppercase text-slate-400 mb-2">
            From year two
          </div>
          <div className="flex items-end gap-2 mb-1">
            <div className="font-heading font-black tracking-tighter text-4xl tabular-nums">
              €50
            </div>
            <div className="text-sm text-slate-400 mb-1">/ year, ex VAT</div>
          </div>
          <p className="text-sm text-slate-300 mt-4">
            Continuous membership renewal. Cancel any time.
          </p>
        </div>
      </div>

      {/* eShop checkout */}
      <div
        className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 sm:p-8"
        data-testid="eshop-embed"
      >
        <div className="text-xs font-medium tracking-[0.2em] uppercase text-slate-500 mb-4">
          Order your membership
        </div>
        <EcwidProduct />
      </div>
    </div>
  );
}
